
  import Vue from "vue";
  import {EVENTS} from "@/utils/events";
  import {ERROR_BANNER_RELOAD_TIMEOUT} from "@/services/ads";
  import {AppActionTypes} from "@/store/modules/app/actions";

  const adClass = 'adsbygoogle';

  export default Vue.extend({
    name: 'pwa-web-ad-banner',
    props: {
      adUnitId: {
        type: String,
        required: true,
      },
      insStyle: {
        type: String,
        default: 'display:inline-block;',
        required: false,
      },
      adFormat: {
        type: String,
        default: '',
        required: false,
      },
      adFullWidthResponsive: {
        type: String,
        default: 'false',
        required: false,
      },
      extraClass: {
        type: String,
        default: '',
        required: false,
      },
      refreshTime: {
        type: Number,
        default: 30000, // milliseconds
        required: false,
      },
    },
    data() {
      return {
        isAdLoadedTimeout: null,
        isAdReloadingTimeout: null,
        errorAdReloadingTimeout: null,
        adKey: 0,
        uniqueValue: Math.random().toString(36).substr(2, 5),
        insElement: null as HTMLElement,
      }
    },
    computed: {
      adClient(): string {
        return process.env.VUE_APP_WEB_ADSENSE_CLIENT_ID;
      },
    },
    mounted() {
      this.loadAd();
      this.$root.$on(EVENTS.RELOAD_WEB_ADS_BANNERS, this.onReloadBanners);
    },
    beforeDestroy() {
      this.clearIsAdLoadedTimeout();
      this.clearIsAdReloadingTimeout();
      this.clearErrorAdReloadingTimeout();
      this.$root.$off(EVENTS.RELOAD_WEB_ADS_BANNERS, this.onReloadBanners);
      this.emitAdLoadedEvent(false);
    },
    methods: {
      loadAd() {
        const adContainerWrapper = this.$refs.adContainerWrapper as HTMLElement;

        if (this.insElement) {
          this.insElement.remove();
          this.insElement = null;
        }

        // Create a new ad element
        this.insElement = document.createElement('ins');
        let className = adClass;
        if (this.extraClass) {
          className += ` ${this.extraClass}`;
        }
        this.insElement.className = className;
        this.insElement.setAttribute('style', this.insStyle);
        this.insElement.setAttribute('data-ad-client', this.adClient);
        this.insElement.setAttribute('data-ad-slot', this.adUnitId);
        if (this.adFormat) {
          this.insElement.setAttribute('data-ad-format', this.adFormat);
        }
        if (this.adFullWidthResponsive) {
          this.insElement.setAttribute('data-full-width-responsive', this.adFullWidthResponsive);
        }

        adContainerWrapper.appendChild(this.insElement);

        (window.adsbygoogle = window.adsbygoogle || []).push({});
        this.startIsAdLoadedTimeout();
        this.startRefreshAdTimeout();
      },
      startRefreshAdTimeout() {
        this.clearIsAdReloadingTimeout();
        if (this.appVisible && this.refreshTime > 0) {
          this.isAdReloadingTimeout = setTimeout(() => {
            this.refreshAd();
          }, this.refreshTime);
        }
      },
      startIsAdLoadedTimeout() {
        this.clearIsAdLoadedTimeout();
        this.isAdLoadedTimeout = setTimeout(() => this.isAdLoaded(), 500);
      },
      refreshAd() {
        this.emitAdLoadedEvent(true);
        this.adKey = this.adKey + 1;
        this.$nextTick(() => {
          setTimeout(() => this.loadAd(), 500);
        });
      },
      isAdLoaded() {
        let loaded = null;
        const adStatus = this.insElement && this.insElement.dataset?.adStatus ? this.insElement.dataset?.adStatus : null;
        if (adStatus) {
          loaded = adStatus === 'filled';
        }

        if (this.insElement && loaded === null) {
          this.startIsAdLoadedTimeout();
          return;
        }

        const adSize = {
          width: 0,
          height: 0,
        };

        if (loaded === false) {
          if (this.appVisible) {
            this.errorAdReloadingTimeout = setTimeout(() => this.onReloadBanners(), ERROR_BANNER_RELOAD_TIMEOUT);
          }
        } else {
          adSize.height = this.insElement.offsetHeight;
          adSize.width = this.insElement.offsetWidth;
        }

        this.emitAdLoadedEvent(loaded, adSize);

        if (!this.$store.getters.firstAdLoaded) {
          this.$store.dispatch(AppActionTypes.SET_FIRST_AD_LOADED, true);
        }
      },
      clearIsAdLoadedTimeout() {
        if (this.isAdLoadedTimeout) {
          clearTimeout(this.isAdLoadedTimeout);
          this.isAdLoadedTimeout = null;
        }
      },
      clearIsAdReloadingTimeout() {
        if (this.isAdReloadingTimeout) {
          clearTimeout(this.isAdReloadingTimeout);
          this.isAdReloadingTimeout = null;
        }
      },
      clearErrorAdReloadingTimeout() {
        if (this.errorAdReloadingTimeout) {
          clearTimeout(this.errorAdReloadingTimeout);
          this.errorAdReloadingTimeout = null;
        }
      },
      emitAdLoadedEvent(loaded: boolean, adSize?: {width: number, height: number}) {
        this.$emit('adLoaded', {loaded, adSize});
      },
      onReloadBanners() {
        this.clearIsAdLoadedTimeout();
        this.clearIsAdReloadingTimeout();
        this.clearErrorAdReloadingTimeout();
        this.refreshAd();
      },
    },
    watch: {
      adUnitId() {
        this.refreshAd();
      },
    },
  });
